import React, { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { AddToCartResponse, BulkAddToCartResponse } from 'services/UserData.Commerce';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import OrderMessages from '../shopping-cart/OrderMessages';

interface AddedToCartAlertProps {
	response: BulkAddToCartResponse;
}

/**
 * Displays confirmation when adding multiple items to cart
 */
export default function MultipleItemsAddedToCartAlert(props: AddedToCartAlertProps) {
	const { response } = props;
	const [displayAlert, setDisplayAlert] = useState(true);

	useEffect(() => {
		setDisplayAlert(true);
	}, [response]);

	if (!displayAlert) {
		return null;
	}

	return (
		<div className="roc-item-summary">
			<OrderMessages orderMessages={response.orderMessages} />
			{response.processedItems &&
				response.processedItems.map((responseItem, index) => {
					return <AddedToCartLineItem item={responseItem} key={index} rowIndex={index} />;
				})}
			<div className="roc-item-summary__section">
				<div className="roc-item-summary__final-wrapper">
					<span className="roc-item-summary__final-count">
						{Localizer('InCart')} {response.cartSummary?.items}
					</span>
					<span className="roc-item-summary__final">
						<span className="roc-item-summary__final-text">{Localizer('Subtotal')}:</span>
						<ProductPricing
							inline
							displayPrice={response.cartSummary?.subtotal}
							className="roc-item-summary__final-value"
						/>
					</span>
				</div>
				<div className="roc-item-summary__btn-row">
					<div className="roc-item-summary__btn-col">
						<button
							type="button"
							onClick={() => (window.location.href = '/cart')}
							className="roc-btn-secondary roc-btn-secondary--big roc-btn-secondary--block roc-item-summary__btn"
							data-testid="view-cart-modal-btn"
						>
							{Localizer('ViewCart')}
						</button>
					</div>
					<div className="roc-item-summary__btn-col">
						<button
							type="button"
							onClick={() => (window.location.href = '/checkout')}
							className="roc-btn roc-btn--big roc-btn--block roc-item-summary__btn"
							data-testid="checkout-modal-btn"
						>
							<svg
								aria-hidden="false"
								aria-label="Close X"
								className="roc-icon roc-icon-lock roc-item-summary__btn-icon"
								height="16"
								role="img"
								viewBox="0 0 32 32"
								width="12"
							>
								<path d="M24.454 14.487h-0.752v-4.385c-0.099-5.608-4.714-10.102-10.374-10.102s-10.275 4.495-10.374 10.102v4.385h-0.743c-1.222 0-2.212 0.982-2.212 2.192v13.129c0 1.211 0.99 2.192 2.212 2.192h22.242c1.222 0 2.212-0.982 2.212-2.192v-13.132c-0.002-1.21-0.992-2.189-2.212-2.189zM19.278 14.487h-11.867v-4.385c0.074-3.191 2.706-5.74 5.927-5.74s5.853 2.549 5.927 5.74l0.013 4.385z" />
							</svg>
							<span className="roc-item-summary__btn-text" data-testid="checkout-now-button">
								{Localizer('CheckoutNow')}
							</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

interface addedToCartLineItemProps {
	item: AddToCartResponse;
	rowIndex?: number;
}

/**
 * Displays a single line item on the alert when adding multiple items to cart
 */
function AddedToCartLineItem(props: addedToCartLineItemProps) {
	const { item, rowIndex } = props;
	return (
		<div className="roc-item-summary__section" data-testid={`added-to-cart-alert-product-${rowIndex}`}>
			<div className="roc-item-summary__item">
				<div className="roc-item-summary__image-wrapper">
					{item.imageUrl ? (
						<img className="roc-item-summary__image" alt={item.name} src={item.imageUrl} />
					) : (
						<img
							className="roc-item-summary__image"
							alt={item.name}
							src="/images/image-not-found-medium.png"
						/>
					)}
				</div>
				<div className="roc-item-summary__text">
					<p>
						<strong>{item.name}</strong>
					</p>

					<ProductPricing inline displayPrice={item.displayPrice} />

					<table className="roc-item-summary__attributes">
						<tbody>
							{item.selections !== null &&
								item.selections.length > 0 &&
								item.selections.map((selection) => (
									<tr
										className="roc-item-summary__row"
										key={`added-to-cart-alert-selection-${selection.name}`}
									>
										<td className="roc-item-summary__col roc-item-summary__col--property">
											<strong>{selection.name}:</strong>
										</td>

										<td className="roc-item-summary__col roc-item-summary__col--value">
											{selection.label}
										</td>
									</tr>
								))}

							<tr className="roc-item-summary__row">
								<td className="roc-item-summary__col roc-item-summary__col--property">
									<strong>{Localizer('Qty')}</strong>
								</td>

								<td className="roc-item-summary__col roc-item-summary__col--value">
									{item.adjustedQuantity}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}
